import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button/Button";
import Grid from "@material-ui/core/Grid/Grid";
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import withWidth, { isWidthUp } from "@material-ui/core/withWidth/withWidth";
import {
  ConfigurateColorsWhiteIcon,
  ConfigurateColorsBlackIcon,
  ConfigurateImprintIcon,
} from "../../../../Icons";

const useStyles = makeStyles(
  (theme) => ({
    grid: {
      margin: theme.spacing(3),
      width: `calc(100% - ${theme.spacing(6)}px)`,
      [theme.breakpoints.up("sm")]: {
        width: `calc(100% - ${theme.spacing(
          6 * theme.spacingMultiplicator.md
        )}px)`,
        margin: theme.spacing(
          2 * theme.spacingMultiplicator.md,
          3 * theme.spacingMultiplicator.md
        ),
      },
      [theme.breakpoints.up("xl")]: {
        margin: theme.spacing(2 * theme.spacingMultiplicator.xl),
        width: `calc(100% - ${
          theme.spacing(4) * theme.spacingMultiplicator.xl
        }px)`,
      },
      "& .MuiGrid-item": {
        "&:first-child": {
          paddingTop: 0,
        },
        "&:last-child": {
          marginTop: theme.spacing(2),
        },
        height: theme.layout.configurationpicker.height,
        maxHeight: theme.layout.configurationpicker.height,
        [theme.breakpoints.up("sm")]: {
          height: theme.layout.configurationpicker.height,
          maxHeight: theme.layout.configurationpicker.height,
          "&:first-child": {
            paddingRight: theme.spacing(1.5 * theme.spacingMultiplicator.md),
            paddingBottom: 0,
            paddingTop: 0,
          },
          "&:last-child": {
            paddingLeft: theme.spacing(1.5 * theme.spacingMultiplicator.md),
            paddingTop: 0,
            paddingBottom: 0,
            marginTop: 0,
          },
        },
        [theme.breakpoints.up("xl")]: {
          height: theme.layout.configurationpicker.heightXL,
          maxHeight: theme.layout.configurationpicker.heightXL,
          "&:first-child": {
            paddingRight: theme.spacing(1 * theme.spacingMultiplicator.xl),
          },
          "&:last-child": {
            paddingLeft: theme.spacing(1 * theme.spacingMultiplicator.xl),
          },
        },
      },
      "& button": {
        fontSize: 14,
        borderRadius: 0,
        border: "1px solid #BFB8AF",
        boxShadow: "none",
        fontWeight: "bold",
        textTransform: "unset",
        height: "100%",
        [theme.breakpoints.up("sm")]: {
          fontSize: 16,
        },
      },
    },
    configBtn: {
      background: theme.palette.configBtn.light,
      color: theme.palette.configBtn.lightText,
      "& .material-icons": {
        color: theme.palette.configBtn.lightText,
      },
      "&:hover": {
        background: "#F2F2F2",
        color: theme.palette.configBtn.lightHover,
        boxShadow: "none",
      },
      "&:focus, &:active": {
        background: "#C9C9C9",
        color: theme.palette.configBtn.lightPressed,
        boxShadow: "none",
      },
    },
    configBtnActive: {
      background: theme.palette.configBtn.light,
      color: theme.palette.configBtn.lightText,
      "& .material-icons": {
        color: theme.palette.configBtn.lightText,
      },
      "&:hover": {
        background: "#F2F2F2",
        color: theme.palette.configBtn.lightText,
        boxShadow: "none",
      },
      "&:focus, &:active": {
        background: "#C9C9C9",
        color: theme.palette.configBtn.lightText,
        boxShadow: "none",
      },
      [theme.breakpoints.up("sm")]: {
        background: theme.palette.configBtn.dark,
        color: theme.palette.configBtn.darkText,
        "& .material-icons": {
          color: theme.palette.configBtn.darkText,
        },
        "&:hover": {
          background: "#959595", // theme.palette.configBtn.darkHover,
          color: theme.palette.configBtn.darkText,
          boxShadow: "none",
        },
        "&:focus, &:active": {
          background: "#585858", // theme.palette.configBtn.darkHover,
          color: theme.palette.configBtn.darkText,
          boxShadow: "none",
        },
      },
    },
    configBtnDisabled: {
      background: "red",
      color: theme.palette.configBtn.lightText,
      "& .material-icons": {
        color: theme.palette.configBtn.lightText,
      },
      "&:hover": {
        background: "#F2F2F2",
        color: theme.palette.configBtn.lightText,
        boxShadow: "none",
      },
      "&:focus, &:active": {
        background: "#C9C9C9",
        color: theme.palette.configBtn.lightText,
        boxShadow: "none",
      },
    },
    buttonIcon: {
      height: "auto",
      width: 25,
      [theme.breakpoints.up("xl")]: {
        width: 30,
      },
      paddingRight: theme.layout.buttonIcon.paddingRight,
    },
  }),
  {
    name: "ConfigurationPicker",
  }
);

const ConfigurationPicker = (props) => {
  const classes = useStyles(props);
  const {
    changeMode,
    selectedConfigurationMode,
    setDisplayCreatorMobilePicker,
    setDisplayDesignerMobilePicker,
    setDesignerModalContentOption,
    designerModalContentOptions,
    setCreatorModalContentOption,
    creatorModalContentOptions,
    width,
    showConfigurationModeSwitch,
  } = props;
  const getDesigner = () => {
    if (selectedConfigurationMode !== "designer") {
      changeMode("designer");
      if (!isWidthUp("sm", width)) {
        setDisplayDesignerMobilePicker(true);
        setDesignerModalContentOption(
          designerModalContentOptions.RENDER_OVERVIEW
        );
      }
    } else if (!isWidthUp("sm", width)) {
      setDisplayDesignerMobilePicker(true);
      setDesignerModalContentOption(
        designerModalContentOptions.RENDER_OVERVIEW
      );
    }
    setCreatorModalContentOption(null);
    setDisplayCreatorMobilePicker(false);
  };
  const getCreator = () => {
    if (selectedConfigurationMode !== "creator") {
      changeMode("creator");
      if (!isWidthUp("sm", width)) {
        setDisplayCreatorMobilePicker(true);
        setCreatorModalContentOption(
          creatorModalContentOptions.RENDER_OVERVIEW
        );
      }
    } else if (!isWidthUp("sm", width)) {
      setDisplayCreatorMobilePicker(true);
      setCreatorModalContentOption(creatorModalContentOptions.RENDER_OVERVIEW);
    }
    setDesignerModalContentOption(null);
    setDisplayDesignerMobilePicker(false);
  };
  const disabledMode = {
    creator:
      !showConfigurationModeSwitch && selectedConfigurationMode === "designer",
    designer:
      !showConfigurationModeSwitch && selectedConfigurationMode === "creator",
  };

  const getButtonClass = (which) => {
    if (disabledMode[which]) {
      return classes.configBtnDisabled;
    }
    if (selectedConfigurationMode === which && isWidthUp("sm", width))
      return classes.configBtnActive;
    return classes.configBtn;
  };

  return (
    <Grid container className={classes.grid}>
      <Grid item xs={12} sm={6}>
        <Button
          className={getButtonClass("creator")}
          variant="contained"
          fullWidth
          size="medium"
          onClick={getCreator}
          disableRipple
          disabled={disabledMode["creator"]}
        >
          {selectedConfigurationMode === "creator" && isWidthUp("sm", width) ? (
            <ConfigurateColorsWhiteIcon className={classes.buttonIcon} />
          ) : (
            <ConfigurateColorsBlackIcon />
          )}
          {t("configurationPicker.creatorBtn")}
        </Button>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Button
          variant="contained"
          className={getButtonClass("designer")}
          fullWidth
          size="medium"
          onClick={getDesigner}
          disableRipple
          disabled={disabledMode["designer"]}
        >
          <ConfigurateImprintIcon className={classes.buttonIcon} />
          {t("configurationPicker.designerBtn")}
        </Button>
      </Grid>
    </Grid>
  );
};

ConfigurationPicker.propTypes = {
  tools: PropTypes.array,
  steps: PropTypes.array,
  variants: PropTypes.array,
  selectedVariantIdentifier: PropTypes.string,
  title: PropTypes.string,
  switchSelectedAmount: PropTypes.func,
  VariantChooserComponent: PropTypes.elementType,
  changeMode: PropTypes.func,
  selectedConfigurationMode: PropTypes.string,
};

ConfigurationPicker.defaultProps = {
  variants: [],
};

export default withWidth({ initialWidth: "lg" })(ConfigurationPicker);
